<template>
    <LayoutDefault variant="white" :hide-logo-on-scroll="true">
        <!-- Filters -->
        <Filters
            :state="openFilters"
            @close="closeFiltersMenu"
            @search="handleSearch"
        />

        <form action="." @submit.prevent="handleSearch" class="bg-black px-3 lg:px-12 py-4">
            <!-- Search -->
            <MoleculeSearchInput v-model="searchValue" class="mt-48"/>

            <TransitionFade>
                <p class="text-white text-center mt-3 opacity-80" v-if="displayMinCharTextInfo">
                    {{ $t('search.min_char_info') }}
                </p>
            </TransitionFade>

            <!-- Tabs -->
            <div class="mt-14 md:mt-24 flex items-center justify-between">
                <div class="flex gap-2 md:gap-6 items-center flex-wrap">
                    <AtomTab
                        v-for="tab in tabs"
                        :key="tab.name"
                        :active="currentTab === tab.name"
                        @click.native="handleTabClick(tab.name)"
                        :name="$t('search.tabs.' + tab.name)"
                        :number="tab.number"
                    />
                </div>
                <button type="button" class="flex items-center gap-4" @click="openFiltersMenu">
                    <AtomIcFilter class="md:hidden" :width="35" :number="numberOfFilters" />
                    <span class="hidden text-white md:block">{{ $t('search.filter') }}</span>
                    <AtomIcFilterAdd class="hidden md:block" :width="35" :number="numberOfFilters"/>
                </button>
            </div>
        </form>

        <!-- Current tab -->
        <div class="min-h-[75vh] bg-darkgray p-3 lg:px-12 lg:pt-10 pt-6">
            <OrganismTabAll
                v-show="currentTab === 'all'"
                :data="all"
                :load="load"
                ref="allTab"
            />

            <OrganismTabArtists
                v-show="currentTab === 'artist'"
                :artists="artists"
                :load="load"
            />

            <OrganismTabEvents
                v-show="currentTab === 'event'"
                :events="events"
                :load="load"
            />
        </div>
    </LayoutDefault>
</template>

<script>
import LayoutDefault from "../../layouts/LayoutDefault.vue";
import {mapActions, mapGetters, mapState} from "vuex";
import router from "../../router";
import Filters from "../../components/search/organisms/OrganismFilters.vue";
import AtomIcFilter from "../../components/search/atoms/AtomIcFilter.vue";
import AtomIcFilterAdd from "../../components/search/atoms/AtomIcFilterAdd.vue";
import AtomTab from "../../components/search/atoms/AtomTab.vue";
import MoleculeSearchInput from "../../components/search/molecules/MoleculeSearchInput.vue";
import OrganismTabEvents from "../../components/search/organisms/OrganismTabEvents.vue";
import OrganismTabArtists from "../../components/search/organisms/OrganismTabArtists.vue";
import OrganismTabAll from "../../components/search/organisms/OrganismTabAll.vue";
import TransitionFade from "../../transitions/TransitionFade.vue";

export default {
    name: 'PageSearch',
    components: {
        OrganismTabAll, OrganismTabArtists, OrganismTabEvents, MoleculeSearchInput, AtomTab, AtomIcFilterAdd,
        AtomIcFilter, LayoutDefault, Filters, TransitionFade
    },
    data(){
        return {
            searchValue: "",
            openFilters: false,
            currentTab: 'all',
            displayMinCharTextInfo: false,
            tabs: [
                { name: 'all', number: 0 },
                { name: 'artist', number: 0 },
                { name: 'event', number: 0 }
            ],
            load: false,
            numberOfFilters: 0
        }
    },

    computed: {
        ...mapGetters('search', ['groupedResults']),
        ...mapState('search', ['lastQuery']),

        all() {
            const project = this.groupedResults?.project || [];
            const event = this.groupedResults?.event || [];
            const people = this.groupedResults?.people || [];

            return [...project, ...event, ...people];
        },
        artists() {
            return this.groupedResults?.people || [];
        },
        events(){
            return this.groupedResults?.event || [];
        }
    },

    methods: {
        ...mapActions('search', ['search']),

        async performSearch() {
            this.displayMinCharTextInfo = false
            this.calculateNumberOfFilters()

            if (this.searchValue.length < 3 && this.numberOfFilters < 1){
                this.displayMinCharTextInfo = true
                return
            }

            this.load = true;

            const { periodStart, periodEnd, selectedSubcategories } = this.$route.query;
            const subcategoriesParams = selectedSubcategories ? selectedSubcategories.split(',').map(Number) : [];

            await this.search(
                {
                    query: this.searchValue,
                    types: null,
                    subcategories: subcategoriesParams,
                    from: periodStart || null,
                    to: periodEnd || null
                });

            this.calculateTabCounts()
            this.load = false;
        },

        calculateTabCounts() {
            const tabCounts = {
                all: (this.groupedResults?.project || []).length + (this.groupedResults?.event || []).length + (this.groupedResults?.people || []).length,
                artist: (this.groupedResults?.people || []).length,
                event: (this.groupedResults?.event || []).length,
            };

            this.tabs.forEach(tab => {
                tab.number = tabCounts[tab.name] || 0;
            });
        },

        calculateNumberOfFilters() {
            const { q, tab, ...filterParams } = this.$route.query;

            this.numberOfFilters = 0

            for (const key in filterParams) {
                if (typeof filterParams[key] === 'string' && filterParams[key].includes(',')) {
                    const arrayValues = filterParams[key].split(',').map(item => item.trim());
                    this.numberOfFilters += arrayValues.length;
                }else {
                    this.numberOfFilters++
                }
            }
        },

        handleSearch (){
            this.performSearch()
            const { q } = this.$route.query;
            if (this.searchValue !== q){
                const currentQuery = this.$route.query;
                const updatedQuery = { ...currentQuery, q: this.searchValue };
                router.push({ query: updatedQuery });
            }
        },

        handleTabClick(tabName) {
            if (this.currentTab !== tabName) {
                const currentQuery = this.$route.query;
                const updatedQuery = { ...currentQuery, tab: tabName };
                router.push({ query: updatedQuery });
                this.currentTab = tabName;

                if (this.currentTab === 'all'){
                    this.$refs['allTab'].forceReRender()
                }
            }
        },

        openFiltersMenu() {
            document.querySelector('body').style.overflow = 'hidden';
            this.openFilters = true
        },

        closeFiltersMenu(){
            document.querySelector('body').style.overflow = '';
            this.openFilters = false
        }
    },

    created() {
        const { q, tab } = this.$route.query;

        if (q) this.searchValue = decodeURIComponent(q);

        if (tab) {
            this.currentTab = tab
        }
    },

    mounted() {
        const { q } = this.$route.query;

        if (this.lastQuery !== q){
            this.performSearch()
        }

        this.calculateTabCounts()
    }
}
</script>
